import tinymce from 'tinymce';
import escape from 'lodash/escape';

type Token = {
  value: string;
  text: string;
};

type CustomFieldValue = {
  custom_field: { permanent_field_id: string };
  display_value?: string | null;
};

// Originally copied from GHO
// https://github.com/grnhse/onboarding/blob/master/app/webpack/tinymce_plugins/tokens.js
tinymce.PluginManager.add('tokens', (editor) => {
  const tokens: Token[] = editor.getParam('tokens', []);
  const valuesForTokens: CustomFieldValue[] = editor.getParam(
    'valuesForTokens',
    []
  );

  function toRichTextPlaceholder(token: Token) {
    const valueForToken = valuesForTokens.find(
      (customFieldValue) =>
        customFieldValue.custom_field.permanent_field_id === token.value
    );
    const dataTokenValue = escape(token.value);

    const useCustomFieldValue =
      valueForToken &&
      valueForToken.display_value &&
      valueForToken.display_value.trim() !== '';

    if (useCustomFieldValue) {
      const value = escape(
        `${token.text}: ${valueForToken.display_value ?? ''}`
      );
      const title = escape(valueForToken.display_value ?? '');
      // eslint-disable-next-line max-len
      return `<input class="token expanded" disabled="disabled" type="button" value="${value}" title="${title}" data-token-value="${dataTokenValue}">`;
    } else {
      const value = escape(token.text);
      // eslint-disable-next-line max-len
      return `<input class="token" disabled="disabled" type="button" value="${value}" data-token-value="${dataTokenValue}">`;
    }
  }

  // `tokens` is read once and drives the chips; `tokens_menu` is read each time the menu opens.
  const menuTokens: (() => Token[]) | undefined =
    editor.getParam('tokens_menu');

  const menuItems = () =>
    (menuTokens?.() ?? tokens).map((token) => ({
      type: 'menuitem' as const,
      text: token.text,
      onAction: () => editor.insertContent(token.value),
    }));

  editor.ui.registry.addMenuButton('tokens', {
    text: editor.getParam('tokens_button_text', 'Insert Token'),
    fetch: (callback) => callback(menuItems()),
    tooltip: editor.getParam('tokens_button_tooltip'),
  });

  /*
    Let's say you have a token like: { value: '{{ phone_number }}', text: 'Phone Number' }
    When the editor is initialized, this will replace each instance of {{ phone_number }} in the editor content
    with elements that look like:
      <input
        type='button'
        class='token'
        disabled='disabled'
        value='Phone Number'
        data-token-value='{{ phone_number }}'
      />
   */
  editor.on('beforeSetContent', function (ed) {
    let content = ed.content;
    tokens.forEach(function (token) {
      // Don't turn the token into an input tag if it's already inside an HTML tag
      // This regex pattern matches the token where it is NOT inside html brackets
      // Not a match: <a href={{TOKEN}} />  Match: <div>{{TOKEN}}</div>
      const tokenRegex = new RegExp(`(?<!<[^>]+)${token.value}(?![^<]*>)`, 'g');
      content = content.replaceAll(tokenRegex, toRichTextPlaceholder(token));
    });
    ed.content = content;
  });

  /*
    This reverses the process of beforeSetContent when the form is submitted.
   */
  const undecorateTokens = (content: string) => {
    tokens.forEach(function (token) {
      const tokenRegex = new RegExp(
        `<input[^>]*data-token-value=\\"${token.value}\\"[^>]*>`,
        'g'
      );
      content = content.replaceAll(tokenRegex, token.value);
    });
    return content;
  };

  editor.on('postProcess', function (e) {
    e.content = undecorateTokens(e.content);
  });
});
